import {
  Divider,
  FormHelperText,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from "@mui/material";
import { useCallback, useEffect, type FC } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { ICustomField } from "@ryde/shared";

import { MuiButton } from "../../../controls";
import { GroupTitleTemplate } from "../group_title.template";
import { ShiftDay } from "./shift-cycle.day";
import {
  getEmptyTemplateLoop,
  getEmptyTemplateWeek,
  shiftTemplateEmptyRow,
  type IShiftDay,
  type TShiftTemplateType,
} from "./shift-cycle.template.models";
import { ShiftCycleTemplateWrapper } from "./shift-cycle.template.styled";

export const ShiftCycleTemplate: FC<ICustomField> = ({
  name,
  formLabel,
  formAttributes,
}) => {
  const fieldNameCycleType = `${name}.type`;
  const fieldNameDays = `${name}.days`;
  const { control, getValues, setValue } = useFormContext();

  const { t } = useTranslation();

  const cycleTypeValue = getValues(fieldNameCycleType) as
    TShiftTemplateType | undefined;
  const isLoop = cycleTypeValue === "loop";

  /* insert sub-items if missing */
  const dayValues = getValues(fieldNameDays);

  const { fields, append, update, remove, replace } = useFieldArray({
    control,
    name: fieldNameDays,
  });

  useEffect(() => {
    if (!dayValues?.length) {
      if (isLoop) {
        setValue(fieldNameDays, getEmptyTemplateLoop());
      } else {
        setValue(fieldNameDays, getEmptyTemplateWeek());
      }
    }
  }, [dayValues, fieldNameDays, isLoop, setValue]);

  const handleChange = useCallback(
    (e: SelectChangeEvent<string>): void => {
      const value = e.target.value as TShiftTemplateType;
      setValue(`${name}.type`, value, {
        shouldDirty: true,
        shouldValidate: true,
      });

      if (value === "weekly") {
        replace(getEmptyTemplateWeek());
      } else if (value === "loop") {
        replace(getEmptyTemplateLoop());
      }
    },
    [name, replace, setValue],
  );

  const handleAppend = (): void => {
    append(shiftTemplateEmptyRow);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: _field, fieldState: { error } }) => (
        <ShiftCycleTemplateWrapper>
          <GroupTitleTemplate
            formLabel={formLabel}
            name={"templateCycle"}
            formAttributes={formAttributes}
          />
          <FormHelperText>{t("shiftTemplate.templateType")}</FormHelperText>
          <Select
            fullWidth
            value={cycleTypeValue || "weekly"}
            onChange={handleChange}
          >
            <MenuItem value="weekly">{t("shiftTemplate.weekly")}</MenuItem>
            <MenuItem value="loop">{t("shiftTemplate.loop")}</MenuItem>
          </Select>
          <Divider sx={{ my: "10px" }} />
          {fields.map((field, index) => (
            <ShiftDay
              key={field.id}
              index={index}
              value={field as unknown as IShiftDay}
              update={update}
              remove={remove}
              isRemoveEnabled={isLoop}
            />
          ))}
          {isLoop && (
            <MuiButton onClick={handleAppend}>
              {t("shiftTemplate.addDayToQueue")}
            </MuiButton>
          )}
          <Divider sx={{ my: "10px" }} />
          {error && <FormHelperText>{error.message as string}</FormHelperText>}
        </ShiftCycleTemplateWrapper>
      )}
    />
  );
};
